import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  InputAdornment,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Chip,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FolderSharedIcon from "@mui/icons-material/FolderShared";

const ShareQuoteDialog = ({
  open,
  onClose,
  users,
  selectedQuotes,
  onShare,
}) => {
  const [search, setSearch] = useState("");
  const [activeQuoteId, setActiveQuoteId] = useState("");
  const [selections, setSelections] = useState({});
  const [isCommonShare, setIsCommonShare] = useState(false);

  const getInitialSelections = (quotes) => {
    const initial = {};
    quotes.forEach((quote) => {
      initial[quote.id] = quote.shared
        ? quote.shared
            .split(",")
            .filter((id) => id)
            .map((id) => id.trim())
        : [];
    });
    return initial;
  };

  useEffect(() => {
    if (open && selectedQuotes?.length > 0) {
      setSelections(getInitialSelections(selectedQuotes));
      setActiveQuoteId(selectedQuotes[0].id);
      setIsCommonShare(false);
    }
  }, [open, selectedQuotes]);

  const handleCommonShareToggle = (checked) => {
    setIsCommonShare(checked);
    
    if (checked) {
      const allSelectedUsers = new Set();
      Object.values(selections).forEach((userList) => {
        userList.forEach((userId) => allSelectedUsers.add(String(userId)));
      });

      const mergedList = Array.from(allSelectedUsers);
      const syncedSelections = {};
      selectedQuotes.forEach((quote) => {
        syncedSelections[quote.id] = [...mergedList];
      });
      setSelections(syncedSelections);
    } else {
      setSelections(getInitialSelections(selectedQuotes));
    }
  };

  const filteredUsers = useMemo(() => {
    const allFiltered =
      users?.filter(
        (u) =>
          u.label?.toLowerCase().includes(search.toLowerCase()) ||
          u.empCode?.toLowerCase().includes(search.toLowerCase())
      ) || [];

    if (search.trim() === "") {
      return allFiltered.slice(0, 50);
    }
    return allFiltered;
  }, [users, search]);

  const handleToggle = (userId) => {
    const idStr = String(userId);
    setSelections((prev) => {
      const currentList = prev[activeQuoteId] || [];
      const isSelected = currentList.includes(idStr);
      const nextList = isSelected
        ? currentList.filter((id) => id !== idStr)
        : [...currentList, idStr];

      if (isCommonShare) {
        const synced = {};
        Object.keys(prev).forEach((key) => {
          synced[key] = nextList;
        });
        return synced;
      } else {
        return { ...prev, [activeQuoteId]: nextList };
      }
    });
  };

  const handleShareSubmit = () => {
    let payload = [];
    if (isCommonShare) {
      payload = [
        {
          project_ids: selectedQuotes.map((q) => q.id),
          user_ids: (selections[activeQuoteId] || []).join(","),
        },
      ];
    } else {
      payload = Object.keys(selections).map((id) => ({
        project_ids: [parseInt(id)],
        user_ids: selections[id].join(","),
      }));
    }
    onShare(payload);
    handleResetAndClose();
  };

  const handleResetAndClose = () => {
    setSelections({});
    setSearch("");
    setIsCommonShare(false);
    onClose();
  };

  const currentSelectedUsers =
    users?.filter((u) =>
      selections[activeQuoteId]?.includes(String(u.value))
    ) || [];

  return (
    <Dialog
      open={open}
      onClose={handleResetAndClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "10px" } }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              p: 1,
              borderRadius: "10px",
              bgcolor: "#0f172a",
              color: "white",
            }}
          >
            <FolderSharedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: "#0f172a" }}
          >
            Share Settings
          </Typography>
        </Box>
        <IconButton onClick={handleResetAndClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 0 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1 }}
        >
          <FormControl sx={{ flex: 1 }}>
            <Select
              disabled={isCommonShare}
              value={activeQuoteId}
              onChange={(e) => setActiveQuoteId(e.target.value)}
              size="small"
              sx={{
                borderRadius: "8px",
                height: "40px",
                bgcolor: isCommonShare ? "#f8fafc" : "white",
                fontSize: "0.85rem",
                fontWeight: 600,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                },
              }}
            >
              {selectedQuotes?.map((quote) => (
                <MenuItem
                  key={quote.id}
                  value={quote.id}
                  sx={{ fontSize: "0.85rem" }}
                >
                  ID: {quote.project_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={isCommonShare}
                onChange={(e) => handleCommonShareToggle(e.target.checked)}
                sx={{
                  p: 0.5,
                  color: "#0f172a",
                  "&.Mui-checked": { color: "#0ea5e9" },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700 }}>
                Common Share
              </Typography>
            }
            sx={{
              m: 0,
              bgcolor: "#f8fafc",
              p: "6px 10px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              height: "40px",
            }}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search team members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": { height: "40px", borderRadius: "8px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            minHeight: "10px",
          }}
        >
          {currentSelectedUsers.map((user) => (
            <Chip
              key={user.value}
              label={user.label}
              size="small"
              onDelete={() => handleToggle(user.value)}
              sx={{
                borderRadius: "6px",
                fontSize: "0.7rem",
                fontWeight: 600,
                bgcolor: "#f1f5f9",
              }}
            />
          ))}
        </Box>

        <List
          sx={{
            maxHeight: 250,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#e2e8f0",
              borderRadius: "10px",
            },
          }}
        >
          {filteredUsers.map((user) => {
            const isSelected = selections[activeQuoteId]?.includes(
              String(user.value)
            );
            return (
              <ListItem
                key={user.value}
                disablePadding
                sx={{
                  mb: 0.5,
                  borderRadius: "8px",
                  bgcolor: isSelected ? "#f0f9ff" : "transparent",
                }}
              >
                <Box
                  onClick={() => handleToggle(user.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    p: 1,
                    cursor: "pointer",
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: isSelected ? "#0ea5e9" : "#e2e8f0",
                      }}
                    >
                      {user.label.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                      >
                        {user.label}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        {user.empCode}
                      </Typography>
                    }
                  />
                  <Checkbox
                    size="small"
                    checked={!!isSelected}
                    sx={{ "&.Mui-checked": { color: "#0ea5e9" } }}
                  />
                </Box>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleResetAndClose}
          sx={{ textTransform: "none", color: "#64748b", fontWeight: 700 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShareSubmit}
          variant="contained"
          disableElevation
          sx={{
            borderRadius: "10px",
            px: 4,
            fontWeight: 700,
            bgcolor: "#0f172a",
            textTransform: "none",
            "&:hover": { bgcolor: "#1e293b" },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareQuoteDialog;